
import { deleteJobAttachment, getAddressLookupData, getJobWithoutLock, setJobLock, uploadJobFiles } from '@/api';
import {
  fetchSetJobEditAllocation,
  fetchSetJobCustomerDetails,
  fetchCreateJobStopData,
  fetchSetJobStopData,
  fetchSetJobStopProductData,
  fetchSetJobStopServiceCodesData,
  fetchJobLock,
  fetchJobUnlock,
  fetchCanScheduleChange,
  fetchSetJobLock,
  fetchSetJobRemarks,
} from '@/helpers/fetchers';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/Button.vue';
import StopDrawing from '@/components/jobDetailed/StopDrawing/StopDrawing.vue';

import ToggleButton from '@/components/UI/ToggleButton.vue';
import DateTimezoneToggle from '@/components/UI/dateTimezoneToggle/DateTimezoneToggle.vue';
import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';
import Icon from '@/components/UI/Icon.vue';
import { getReferencesParamsWrapper } from '@/helpers/booking';

import JobComplianceModal from '@/components/jobBooking/JobComplianceModal.vue';
import AddRolledServiceCodeDialog from '@/components/jobBooking/AddRolledServiceCodeDialog.vue';

import { Job, User } from '@/models/main';
import { AddressBookLookupData } from '@/models/addressBook';

import {
  JobComplianceData,
  JobComplianceHiddenData,
  JobComplianceEditingSections,
  JobComplianceLoaders,
  JobComplianceDataFetchResponse,
  JobComplianceProductsDataFetchResponse,
  JobComplianceDiffData,
} from '@/models/JobComplianceTypes';

import { mapState } from 'vuex';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as jsonpatch from 'fast-json-patch/index.mjs';
import { notifyNetworkError, notifyNetworkSuccess } from '@/helpers/notifiers';
import * as Sentry from '@sentry/vue';
import { JobLockByUserError, JobLockError } from '@/helpers/throwables';
import { checkJobDatesChanged } from '@/utils/Common';

@Component({
  components: {
    AddRolledServiceCodeDialog,
    BackButton,
    Button,
    DatePicker,
    DateTimezoneToggle,
    JobComplianceModal,
    Icon,
    LookupSuburb,
    StopDrawing,
    ToggleButton,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class JobCompliance extends Vue {
  @Prop({ type: Array, required: true, default: false })
  public tableData: Job[];
  @Prop({ type: Boolean, required: true, default: false })
  public showModal: boolean;
  @Prop({ type: Number, required: true, default: null })
  public currentJobIdx: number;

  private user!: User;

  public isAddRolledServiceCodeDialogOpen = false;
  public rolledServiceCodeStopNumber = -1;

  public loaders: JobComplianceLoaders = {
    singleJob: false,
    driver: false,
    canSchedule: false,
    cancel: false,
    futile: false,
    splitToBase: false,
    stairsService: false,
    remove: false,
    saving: false,
    sectionLoading: null,
  };

  public animatePanel = false;

  public current = 0;
  public direction = 1;
  public transitionName = 'fade';
  public jobProcessingState = {
    hasError: '',
    cannotBeEdited: '',
    hasChanged: false,
    isEditingByOther: false,
  };
  public searchedJobsClone: Job[] = [];

  private editSectionForm: null | HTMLFormElement = null;

  public editingSections: JobComplianceEditingSections = {
    customerDetails: false,
    driver: false,
    stop0: false,
    stop1: false,
    stop2: false,
    products0: false,
    services0: false,
    products1: false,
    services1: false,
    files_list: false,
    files_upload: false,
    remarks: false,
  };

  public formData: JobComplianceData = {
    rockhopper_id: 0,
    rockhopper_date: '',
    booked_by: '',
    rockhopper_key: 0,
    cancelled_at: '',
    booked_at: '',
    allocated_at: '',
    completed_at: '',
    price: '',
    reference_1: '',
    invoice_status: 'Pending',
    payment_status: 'Pending',
    reference_2: '',
    reference_3: '',
    reference_4: '',
    customer_ref1_prompt: '',
    customer_ref2_prompt: '',
    customer_ref3_prompt: '',
    customer_ref4_prompt: '',
    is_rfc: true,
    customer: '',
    status: '',
    wh_status: '',
    main_stop: 0,
    service_area: 0,
    split_base: 0,
    ar_jobs_count: 0,
    stops: [
      {
        stop_number: 1,
        arrived_at: '',
        departed_at: null,
        required_at: '',
        pod_time: null,
        pod_name: null,
        is_pod_image: false,
        is_warehouse: false,
        driver_is: null,
        equipment_code: '',
        name: '',
        address_line_1: '',
        address_line_2: '',
        suburb: '',
        post_code: '',
        state: '',
        attributes: [],
        instructions: '',
        time_earliest: '',
        time_latest: '',
        schedule_start: '',
        schedule_finish: '',
        can_schedule: false,
        pickup_products: {},
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        app_images: [],
        services: [],
        duration: 0,
      },
      {
        stop_number: 2,
        arrived_at: '',
        departed_at: null,
        required_at: '',
        pod_time: null,
        pod_name: null,
        is_pod_image: false,
        is_warehouse: false,
        driver_is: null,
        equipment_code: '',
        name: '',
        address_line_1: '',
        address_line_2: '',
        suburb: '',
        post_code: '',
        state: '',
        attributes: [],
        instructions: '',
        time_earliest: '',
        time_latest: '',
        schedule_start: '',
        schedule_finish: '',
        can_schedule: false,
        pickup_products: {},
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        app_images: [],
        services: [],
        duration: 0,
      },
      {
        stop_number: 3,
        arrived_at: '',
        departed_at: null,
        required_at: '',
        pod_time: null,
        pod_name: null,
        is_pod_image: false,
        is_warehouse: false,
        driver_is: null,
        equipment_code: '',
        name: '',
        address_line_1: '',
        address_line_2: '',
        suburb: '',
        post_code: '',
        state: '',
        attributes: [],
        instructions: '',
        time_earliest: '',
        time_latest: '',
        schedule_start: '',
        schedule_finish: '',
        can_schedule: false,
        pickup_products: {},
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        app_images: [],
        services: [],
        duration: 0,
      },
    ],
    notes: [],
    zammad_tickets: [],
    messages: [],
    attachments: [],
    delay: [],
    caller: '',
    phone: '',
    warnings: [],
    allocation_state: 'deallocated',
    schedule_board: {},
    remarks: '',
    special_instructions: [],
  };
  private formDataSaved = JSON.stringify(this.formData);

  public jobHiddenData: JobComplianceHiddenData = {
    canSchedule: null,
    ref1_prompt: '',
    ref2_prompt: '',
    ref3_prompt: '',
    ref4_prompt: '',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
    ref4_required: false,
    driverCode: null,
    driverStatus: 'deallocated',
    driverStatusChosen: null,
    cancel_name: '',
    cancel_reason: '',
    futile_name: 0,
    futile_reason: '',
    split_base: 0,
    split_stop_number: 1,
    stairs_stop_number: 1,
    dateTimes: [
      {
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeFromInitial: '',
        timeTo: '',
        timeToInitial: '',
        dateIsLinked: false,
      },
      {
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeFromInitial: '',
        timeTo: '',
        timeToInitial: '',
        dateIsLinked: false,
      },
      {
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeFromInitial: '',
        timeTo: '',
        timeToInitial: '',
        dateIsLinked: false,
      },
    ],
    filesToUpload: [],
    filesToRemove: [],
    driverButtonsState: {
      deallocate: 'off',
      preallocate: 'off',
      allocate: 'off',
    },
  };
  private jobHiddenDataSaved = JSON.stringify(this.jobHiddenData);

  public addressBookData: AddressBookLookupData[] = [];

  //

  @Watch('showModal', { immediate: true })
  private watchToShowModal() {
    if (this.showModal) {
      this.searchedJobsClone = structuredClone(this.tableData);
    }
  }

  @Watch('currentJobIdx', { immediate: true, deep: true })
  private currentJobIdxChanged() {
    this.current = this.currentJobIdx;
    this.fetchDataById(
      this.searchedJobsClone[this.currentJobIdx].id,
      this.searchedJobsClone[this.currentJobIdx].job_date,
    );
  }

  public slide(dir: number): void {
    if (this.currentJobIdx >= 0) {
      this.direction = dir;
      dir === 1 ? (this.transitionName = 'slide-next') : (this.transitionName = 'slide-prev');
      const len = this.searchedJobsClone.length;
      this.current = (this.current + (dir % len) + len) % len;
      this.$emit('currentJobChanged', this.current);
    }
  }

  private getAddressBookWrapper(): void {
    getAddressLookupData(this.formData.customer)
      .then((res: AddressBookLookupData[]) => {
        this.addressBookData = res;
      })
      .catch(err => {
        Sentry.withScope(scope => {
          scope.setLevel('warning');
          Sentry.captureException(err);
        });
      });
  }

  public async canScheduleClicked(): Promise<void> {
    if (this.jobHiddenData.canSchedule === null) {
      return;
    }
    this.loaders.canSchedule = true;
    const canSchedule = this.jobHiddenData.canSchedule;
    await fetchCanScheduleChange(!canSchedule, this.formData.rockhopper_id, this.formData.rockhopper_date)
      .then(() => {
        // Toggle the canSchedule state upon successful completion of the fetch function
        this.jobHiddenData.canSchedule = !canSchedule;
        // Changing the `can_schedule` flag on all stops, because of the "diff"
        this.formData.stops.forEach(stop => (stop.can_schedule = !stop.can_schedule));
      })
      .catch(() => {
        this.jobHiddenData.canSchedule = canSchedule;
      });
    this.loaders.canSchedule = false;
  }

  // FETCHERS
  //
  public async fetchDataById(id: number, date: string, loaders?: boolean) {
    this.jobProcessingState.hasError = '';
    this.jobProcessingState.cannotBeEdited = '';
    this.jobProcessingState.hasChanged = false;

    if (loaders !== false) {
      this.loaders.singleJob = true;
      this.loaders.canSchedule = true;
    }

    // fetchJobLock
    await getJobWithoutLock(id, date)
      .then(async data => {
        this.formData = { ...data };
        // Driver section apply
        let currentDriver: number | null = null;
        if (this.formData.allocation_state !== 'deallocated') {
          const drivers: boolean[] = this.formData.stops.map(stop => !!stop.driver_is);
          currentDriver = this.formData.stops[drivers.lastIndexOf(true)].driver_is;
        } else {
          currentDriver = null;
        }
        this.jobHiddenData.driverCode = currentDriver;
        this.jobHiddenData.canSchedule = this.formData.stops[0].can_schedule;
        await getReferencesParamsWrapper(this.formData.customer, this.jobHiddenData);
        this.getAddressBookWrapper();
        // Clear errors:
        this.jobProcessingState.cannotBeEdited = '';
        this.jobProcessingState.hasChanged = false;
        this.jobProcessingState.isEditingByOther = false;
        this.jobProcessingState.hasError = '';
      })
      .catch(err => {
        if (err.response?.status) {
          this.jobProcessingState.cannotBeEdited = `
            The job is used in another system
            ${err.response.data.error}.
            Wait for the job to be unlocked or ask a manager to enforce its unlocking
          `;
          return;
        }
        this.jobProcessingState.cannotBeEdited = 'Something went wrong during loading';
      })
      .finally(() => {
        if (loaders !== false) {
          this.loaders.canSchedule = false;
          this.loaders.singleJob = false;
        }
      });
  }

  public addTmp3rdStop() {
    if (!this.formData.stops[2]) {
      this.formData.stops[2] = {
        stop_number: 3,
        arrived_at: '',
        departed_at: null,
        required_at: '',
        pod_time: null,
        pod_name: null,
        is_pod_image: false,
        is_warehouse: false,
        driver_is: null,
        equipment_code: '',
        name: '',
        address_line_1: '',
        address_line_2: '',
        suburb: '',
        post_code: '',
        state: this.formData.stops[1].state,
        attributes: [],
        instructions: '',
        time_earliest: this.formData.stops[1].time_earliest,
        time_latest: this.formData.stops[1].time_latest,
        schedule_start: '',
        schedule_finish: '',
        can_schedule: false,
        pickup_products: {},
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        app_images: [],
        services: [],
        duration: 1,
      };
      this.formData.stops[2]._isTmp = true;
      this.formData = { ...this.formData };
      this.$nextTick(() => {
        const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
        if (jobComplianceForm) {
          jobComplianceForm.applyStopsData();
        }
      });
    }
  }

  public removeTmp3rdStop() {
    if (this.formData.stops[2]?._isTmp) {
      this.formData.stops = this.formData.stops.slice(0, 2);
      this.formData = { ...this.formData };
      this.$nextTick(() => {
        const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
        if (jobComplianceForm) {
          jobComplianceForm.applyStopsData();
        }
      });
    }
  }

  public async fetchJobReload(loaders?: boolean, delay?: boolean) {
    if (delay) {
      this.loaders.singleJob = true;
      this.loaders.canSchedule = true;
      setTimeout(async () => {
        await this.fetchDataById(
          this.searchedJobsClone[this.current].id,
          this.searchedJobsClone[this.current].job_date,
          loaders,
        );
      }, 5000);
    } else {
      await this.fetchDataById(
        this.searchedJobsClone[this.current].id,
        this.searchedJobsClone[this.current].job_date,
        loaders,
      );
    }
    await this.closeEditingSections();
  }

  public async fetchUnlockedJob(): Promise<void> {
    const lockRes = await this.checkUpdateDataPossibility(this.formData.rockhopper_id, this.formData.rockhopper_date);
    if (!lockRes) {
      await this.applyBtnClick();
    }
    this.jobProcessingState.hasChanged = lockRes;
  }

  // Returns false if job is locked again and ready for data injection
  // Returns true if the job cant be changed
  private async checkUpdateDataPossibility(id, date) {
    const setLock = await fetchJobLock(id, date);
    if (typeof setLock !== 'boolean' && setLock.locked_by === this.user.username) {
      const diff = jsonpatch.compare(setLock.job, JSON.parse(this.formDataSaved));
      if (!diff.length) {
        return false;
      }
    }
    return true;
  }

  public callAnimatePanel(): void {
    this.animatePanel = true;
    setTimeout(() => {
      this.animatePanel = false;
    }, 10000);
  }

  public getOpenSectionName() {
    let sectionName = '';
    Object.keys(this.editingSections).some(section => {
      if (this.editingSections[section] === true) {
        sectionName = section;
      }
    });
    return sectionName;
  }

  public async handleSetJobLock(section: string): Promise<void> {
    if (section) {
      this.editingSections[section] = true;
      this.loaders.sectionLoading = section;
      this.jobProcessingState.cannotBeEdited = '';
    }

    await fetchSetJobLock(this.formData.rockhopper_id, this.formData.rockhopper_date)
      .then(() => {
        this.jobProcessingState.cannotBeEdited = '';
        this.jobProcessingState.hasChanged = false;
        this.jobProcessingState.isEditingByOther = false;
        this.jobProcessingState.hasError = '';
      })
      .catch(err => {
        if (err instanceof JobLockError) {
          this.jobProcessingState.cannotBeEdited = `
            The job is used in another system:
            ${err}.
            Wait for the job to be unlocked or ask a manager to enforce its unlocking
          `;
          return;
        } else if (err instanceof JobLockByUserError) {
          this.jobProcessingState.cannotBeEdited = `The job is being edited by ${err.message}`;
          this.editingSections[section] = false;
          return;
        }
        this.jobProcessingState.cannotBeEdited = 'Something went wrong during locking';
      })
      .finally(() => {
        this.loaders.sectionLoading = null;
      });
  }

  public async editSectionClicked(section, form): Promise<void> {
    this.editSectionForm = form;
    if (section === 'stop2' && this.formData.stops[2]._isTmp) {
      // this.formDataSaved should be JSON from ths.formData without stops[2]
      this.formDataSaved = JSON.stringify({
        ...this.formData,
        stops: this.formData.stops.slice(0, 2),
      });
    } else {
      this.formDataSaved = JSON.stringify(this.formData);
    }
    this.jobHiddenDataSaved = JSON.stringify(this.jobHiddenData);
    await this.handleSetJobLock(section);
  }

  public async closeEditingSections(): Promise<void> {
    Object.keys(this.editingSections).map(section => {
      this.editingSections[section] = false;
    });
    this.editSectionForm = null;
    this.jobProcessingState.cannotBeEdited = '';
    await fetchJobUnlock(this.formData.rockhopper_id, this.formData.rockhopper_date);
  }

  public async reallocateDriver(params) {
    const lock = await fetchJobLock(this.formData.rockhopper_id, this.formData.rockhopper_date);
    if (lock) {
      const result = await fetchSetJobEditAllocation(
        this.formData.rockhopper_id,
        this.formData.rockhopper_date,
        params,
      );
      if (result) {
        await this.fetchDataById(this.formData.rockhopper_id, this.formData.rockhopper_date, false);
        this.$emit('updateTable');
        await this.closeEditingSections();
      }
    } else {
      notifyNetworkError({ title: 'Something went wrong' });
    }
  }

  public async cancelBtnClick(): Promise<void> {
    this.jobProcessingState.hasError = '';

    const sectionName = this.getOpenSectionName();
    const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;

    if (jobComplianceForm) {
      const prevData = JSON.parse(this.jobHiddenDataSaved);
      const prevDriver = prevData.driverCode;
      switch (sectionName) {
        case 'customerDetails':
          this.formData = JSON.parse(this.formDataSaved);
          break;
        case 'driver':
          this.jobHiddenData.driverCode = prevDriver;
          this.jobHiddenData.driverStatusChosen = null;
          await jobComplianceForm.applyDriverData();
          break;
        case 'stop0':
        case 'stop1':
        case 'stop2':
          this.formData.stops = JSON.parse(this.formDataSaved).stops;
          await jobComplianceForm.applyStopsData();
          break;
        case 'products0':
          this.formData.stops = await JSON.parse(this.formDataSaved).stops;
          await jobComplianceForm.applyProductsData(0);
          break;
        case 'products1':
          this.formData.stops = await JSON.parse(this.formDataSaved).stops;
          await jobComplianceForm.applyProductsData(1);
          break;
        case 'services0':
          this.formData.stops = await JSON.parse(this.formDataSaved).stops;
          await jobComplianceForm.applyServicesData(0);
          break;
        case 'services1':
          this.formData.stops = await JSON.parse(this.formDataSaved).stops;
          await jobComplianceForm.applyServicesData(1);
          break;
        case 'files_list':
          this.jobHiddenData.filesToRemove = [];
          break;
        case 'files_upload':
          this.jobHiddenData.filesToUpload = [];
          break;
        case 'remarks':
          this.formData.remarks = JSON.parse(this.formDataSaved).remarks;
          break;
        default:
          this.formData = JSON.parse(this.formDataSaved);
          break;
      }
    }

    this.editSectionForm?.reset();
    await this.closeEditingSections();
  }

  public async create3rdStop(params): Promise<void> {
    // Remove _isTmp flag from the params
    params.forEach(item => {
      if (item.value && item.value._isTmp !== undefined) {
        delete item.value._isTmp;
      }
    });
    const result = await fetchCreateJobStopData(this.formData.rockhopper_id, this.formData.rockhopper_date, 3, params);
    if (result.status === 'unlocked') {
      const setLock = await fetchJobLock(this.formData.rockhopper_id, this.formData.rockhopper_date);
      if (!setLock) {
        notifyNetworkError({ title: 'Something went wrong' });
        return;
      }
      if (typeof setLock !== 'boolean' && setLock.locked_by === this.user.username) {
        await this.create3rdStop(params);
      }
    } else if (result.status === 'ok') {
      this.$emit('updateTable');
      await this.fetchJobReload();
    } else if (result.status === 'error' && result.error) {
      this.jobProcessingState.hasError = result.error;
    } else {
      notifyNetworkError({ title: 'Failed to create stop data' });
    }
  }

  public async applyBtnClick(): Promise<void> {
    const sectionName = this.getOpenSectionName();
    const diff = jsonpatch.compare(JSON.parse(this.formDataSaved), this.formData);

    const noValidationSections = ['files_list', 'files_upload'];
    if (!noValidationSections.includes(sectionName)) {
      const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
      const isProductsSection = sectionName?.includes('products');
      const isServicesSection = sectionName?.includes('services');

      if (isProductsSection || isServicesSection) {
        const stopIndex = Number(sectionName.slice(-1));
        const sectionRef = jobComplianceForm.$refs[`${sectionName}`] as HTMLFormElement[];
        if (!(await sectionRef?.[0]?.validate())) {
          return;
        }
      } else if (!(await this.editSectionForm?.validate())) {
        return;
      }

      if (!diff.length && sectionName !== 'driver') {
        await this.closeEditingSections();
        this.jobProcessingState.isEditingByOther = false;
        return;
      }
    }

    try {
      this.loaders.saving = true;

      switch (sectionName) {
        case 'customerDetails':
          await this.handleCustomerDetailsSection(diff);
          break;
        case 'stop0':
        case 'stop1':
        case 'stop2':
          await this.handleStopSection(diff, sectionName);
          break;
        case 'products0':
        case 'products1':
          await this.handleProductSection(diff, sectionName);
          break;
        case 'services0':
        case 'services1':
          await this.handleServiceSection(diff, sectionName);
          break;
        case 'driver':
          await this.handleDriverSection();
          break;
        case 'files_list':
          await this.handleFilesListSection();
          break;
        case 'files_upload':
          await this.handleFilesUploadSection();
          break;
        case 'remarks':
          await this.handleRemarksSection(diff);
          break;
      }
    } finally {
      this.loaders.saving = false;
    }
  }

  private async handleCustomerDetailsSection(diff: any[]): Promise<void> {
    const res = await fetchSetJobCustomerDetails(this.formData.rockhopper_id, this.formData.rockhopper_date, diff);
    this.sendUpdateHandler(res);
  }

  private async handleRemarksSection(diff: any[]): Promise<void> {
    const res = await fetchSetJobRemarks(this.formData.rockhopper_id, this.formData.rockhopper_date, diff);
    this.sendUpdateHandler(res);
  }

  private async handleStopSection(diff: any[], sectionName: string): Promise<void> {
    if (sectionName === 'stop2' && this.formData.stops[2]._isTmp) {
      await this.create3rdStop(diff);
    } else {
      const res = await fetchSetJobStopData(
        this.formData.rockhopper_id,
        this.formData.rockhopper_date,
        Number(sectionName.slice(-1)) + 1,
        diff,
      );
      this.sendUpdateHandler(res);
    }
  }

  private async handleProductSection(diff: any[], sectionName: string): Promise<void> {
    const res = await fetchSetJobStopProductData(
      this.formData.rockhopper_id,
      this.formData.rockhopper_date,
      Number(sectionName.slice(-1)) + 1,
      diff,
    );
    if (res.data) {
      this.formData.stops[Number(sectionName.slice(-1))].pickup_products = res.data;
      const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
      await jobComplianceForm.applyProductsData(Number(sectionName.slice(-1)));
    }
    this.sendUpdateHandler(res);
  }

  private async handleServiceSection(diff: any[], sectionName: string): Promise<void> {
    const legIndex = Number(sectionName.slice(-1));
    const res = await fetchSetJobStopServiceCodesData(
      this.formData.rockhopper_id,
      this.formData.rockhopper_date,
      legIndex + 1,
      diff,
    );
    if (res.data) {
      this.formData.stops[legIndex].services = res.data;
      const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
      await jobComplianceForm.applyServicesData(legIndex);
    }
    this.sendUpdateHandler(res);
    // Update kraken price if service section is saved
    if (res.status === 'ok') {
      this.$nextTick(async () => {
        const jobComplianceModal = this.$refs['jobComplianceForm'] as HTMLFormElement;
        if (jobComplianceModal && typeof jobComplianceModal.getKrakenPriceData === 'function') {
          await jobComplianceModal.getKrakenPriceData(legIndex);
        }
      });
    }
  }

  private async handleDriverSection(): Promise<void> {
    if (
      (this.jobHiddenData.driverStatusChosen === 'allocate' || this.jobHiddenData.driverStatusChosen === null) &&
      this.formData.allocation_state === 'allocated'
    ) {
      notifyNetworkError({ text: 'Driver is already allocated' });
      return;
    }

    const action =
      this.formData.allocation_state === 'allocated' && this.jobHiddenData.driverStatusChosen !== 'preallocate'
        ? 'deallocate_driv'
        : this.jobHiddenData.driverStatusChosen;
    const params = {
      action: action ?? 'not-null',
      job_id: this.formData.rockhopper_id,
      job_date: this.formData.rockhopper_date,
      driver: this.jobHiddenData.driverCode,
      stop_number: this.formData.stops.length,
    };
    const result = await fetchSetJobEditAllocation(this.formData.rockhopper_id, this.formData.rockhopper_date, params);
    if (result.status === 'unlocked') {
      await this.reallocateDriver(params);
    } else if (result.status === 'ok') {
      this.$emit('updateTable');
      await this.fetchJobReload(false);
      const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
      await jobComplianceForm.applyDriverData();
    }
  }

  private async handleFilesListSection(): Promise<void> {
    if (this.jobHiddenData.filesToRemove.length > 0) {
      const removeResult = await Promise.all(
        this.jobHiddenData.filesToRemove.map(id => {
          return deleteJobAttachment(id)
            .then(() => {
              return true;
            })
            .catch(() => {
              return false;
            });
        }),
      );

      if (removeResult.every(x => x === true)) {
        this.jobHiddenData.filesToRemove.map(id => {
          const idxToRemove = this.formData.attachments.findIndex(x => x.id === id);
          if (idxToRemove >= 0) {
            this.formData.attachments.splice(idxToRemove, 1);
          }
        });
        notifyNetworkSuccess({ text: 'Successfully deleted' });
        this.jobHiddenData.filesToRemove = [];
        await this.closeEditingSections();
      } else {
        notifyNetworkError({ title: 'Error while deleting files' });
      }
    } else {
      await this.closeEditingSections();
    }
  }

  private async handleFilesUploadSection(): Promise<void> {
    if (this.jobHiddenData.filesToUpload.length > 0) {
      const files: File[] = [];
      this.jobHiddenData.filesToUpload.map(file => {
        if (file.file_data) {
          files.push(file.file_data);
        }
      });

      const params = {
        rh_id: this.formData.rockhopper_id,
        rh_date: this.formData.rockhopper_date,
        files: files,
      };

      await uploadJobFiles(params)
        .then(res => {
          this.formData.attachments = [...this.formData.attachments, ...res.data.saved];
          this.jobHiddenData.filesToUpload = [];
          this.closeEditingSections();
          notifyNetworkSuccess({ text: 'Successfully uploaded' });
        })
        .catch(() => {
          notifyNetworkError({ title: 'Attachments upload failed' });
        });
    } else {
      await this.closeEditingSections();
    }
  }

  public closeAddRolledServiceDialog() {
    this.isAddRolledServiceCodeDialogOpen = false;
  }

  /**
   * This function is used to add 'ROLLED' service code to the 2nd last stop.
   * It first checks if the stop already has 'ROLLED' service code, if it does, it increases the quantity by 1,
   * if it doesn't, it adds 'ROLLED' service code with quantity of 1.
   * It then calls handleSetJobLock to lock the job and then fetchSetJobStopServiceCodesData to update the job data.
   * If the request is successful, it updates the formData and applies the new data to the form.
   * It then calls `sendUpdateHandler` to update the job data in the parent component.
   * Finally it closes the 'Add ROLLED service code' dialog.
   */
  public async addRolledServiceCode(): Promise<void> {
    const changedStopNumber = this.rolledServiceCodeStopNumber;
    if (changedStopNumber === -1) {
      return;
    }
    this.loaders.saving = true;
    const changedStopServicesCounter = this.formData.stops[changedStopNumber].services.length;
    const changedStopServices = this.formData.stops[changedStopNumber].services;
    const changedStopWithRolledSCIdx = changedStopServices.findIndex(x => x.name === 'ROLLED');
    let reqChange: JobComplianceDiffData[] = [];

    if (changedStopWithRolledSCIdx >= 0) {
      reqChange = [
        {
          op: 'replace',
          path: `/stops/${changedStopNumber}/services/${changedStopWithRolledSCIdx}/quantity`,
          value: changedStopServices[changedStopWithRolledSCIdx].quantity + 1,
        },
      ];
    } else {
      reqChange = [
        {
          op: 'add',
          path: `/stops/${changedStopNumber}/services/${changedStopServicesCounter}`,
          value: {
            name: 'ROLLED',
            quantity: 1,
            pay: 0,
            scheduled_fee: 0,
            second_quantity: 0,
            gst: 0,
            fee: 0,
          },
        },
      ];
    }

    await this.handleSetJobLock('');

    try {
      const res = await fetchSetJobStopServiceCodesData(
        this.formData.rockhopper_id,
        this.formData.rockhopper_date,
        changedStopNumber + 1,
        reqChange,
      );
      if (res?.data) {
        this.formData.stops[changedStopNumber].services = res.data;
        const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
        await jobComplianceForm.applyServicesData(changedStopNumber);
        this.sendUpdateHandler(res);
      }
      this.isAddRolledServiceCodeDialogOpen = false;
    } catch (error) {
      notifyNetworkError({ title: "Can't add ROLLED service code" });
    }

    this.loaders.saving = false;
  }

  private async sendUpdateHandler(
    res: JobComplianceDataFetchResponse | JobComplianceProductsDataFetchResponse,
  ): Promise<void> {
    switch (res.status) {
      case 'ok':
        this.jobProcessingState.isEditingByOther = false;
        // Date changed case:
        if (res.job) {
          // Current job data:
          this.formData = { ...res.job };
          // Apply stop data to change dates and times in `jobHiddenData`
          const jobComplianceForm = this.$refs['jobComplianceForm'] as HTMLFormElement;
          jobComplianceForm.applyStopsData(res.job.stops);
          // Show 'ROLLED' dialog if dates has changed
          this.isAddRolledServiceCodeDialogOpen = checkJobDatesChanged(
            JSON.parse(this.formDataSaved),
            this.formData,
          )[0];

          this.rolledServiceCodeStopNumber = checkJobDatesChanged(JSON.parse(this.formDataSaved), this.formData)[1];
          const idx = this.searchedJobsClone.findIndex(x => Number(x.id) === res.job?.rockhopper_id);
          this.searchedJobsClone[idx].job_date = res.job.rockhopper_date;
          // Parent table update:
          this.$emit('updateTable', res.job.rockhopper_date);
          // Close aka unlock should be after updating with new job date to unlock with correct date
          this.closeEditingSections();
          break;
        }
        // Show 'ROLLED' dialog if dates has changed
        this.isAddRolledServiceCodeDialogOpen = checkJobDatesChanged(JSON.parse(this.formDataSaved), this.formData)[0];
        this.rolledServiceCodeStopNumber = checkJobDatesChanged(JSON.parse(this.formDataSaved), this.formData)[1];
        this.$emit('updateTable');
        this.closeEditingSections();
        break;
      case 'unlocked':
        this.fetchUnlockedJob();
        break;
      case 'editing':
        this.jobProcessingState.isEditingByOther = true;
        break;
      case 'error':
        if (res.error) {
          this.jobProcessingState.hasError = res.error;
        }
        break;
    }
  }
  //
}
